import { DocumentQuery, IDocumentService, SortOrder } from '@price-for-profit/micro-services';
import { CLIENT_ID, DRAFT_NAME_CHANGE_DOCUMENT_TYPE } from 'shared/constants/app';
import { CustomerNameChange, SortableDraftNameChangeFields } from 'shared/types';
import { isAxiosError } from 'shared/utility';

export interface IDraftCustomerNameChangeService {
  createNewDraftNameChange: (customerNameChangeTerms: CustomerNameChange) => Promise<CustomerNameChange>;
  deleteDraftNameChange(id: string): Promise<void>;
  updateDraftNameChange: (nameChangeTerms: CustomerNameChange) => Promise<{ operationId: string }>;
  getDraftNameChange: (id: string) => Promise<CustomerNameChange | undefined>;
  getDraftNameChanges: (baseTableProps: TableProps) => Promise<GetDraftNameChangesResponse>;
  getNameChangeDataForMasterCustomerName: (nameChange: string) => Promise<CustomerNameChange[] | undefined>;
}
export interface GetDraftNameChangesResponseOne {
  nameChanges: CustomerNameChange[];
}

export interface GetDraftNameChangesResponse {
  nameChanges: CustomerNameChange[];
  totalCount: number;
  totalPages: number | undefined;
  currentPage: number;
}

export interface TableProps {
  page?: number;
  pageSize?: number;
  sortOrder?: SortOrder;
  sortBy?: SortableDraftNameChangeFields;
}

export class DraftCustomerNameChangeService implements IDraftCustomerNameChangeService {
  private shared: { type: string; clientId: string } = { type: DRAFT_NAME_CHANGE_DOCUMENT_TYPE, clientId: CLIENT_ID };
  constructor(private documentService: IDocumentService) {}

  async deleteDraftNameChange(id: string): Promise<void> {
    await this.documentService.delete({
      ...this.shared,
      id,
    });
  }

  async createNewDraftNameChange(nameChange: CustomerNameChange): Promise<CustomerNameChange> {
    const { data } = await this.documentService.create<CustomerNameChange, CustomerNameChange>({
      ...this.shared,
      payload: nameChange,
    });

    return data;
  }

  async updateDraftNameChange(nameChange: CustomerNameChange): Promise<{ operationId: string }> {
    const { metadata } = await this.documentService.update<CustomerNameChange>({
      ...this.shared,
      id: nameChange.id,
      payload: nameChange,
    });

    return metadata;
  }

  async getDraftNameChange(id: string): Promise<CustomerNameChange | undefined> {
    try {
      const { data } = await this.documentService.getById<CustomerNameChange>({
        clientId: CLIENT_ID,
        type: DRAFT_NAME_CHANGE_DOCUMENT_TYPE,
        id,
      });
      return data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error?.response?.status === 404) {
          return undefined;
        }
        throw error;
      }
      throw error;
    }
  }

  async getDraftNameChanges(baseTableProps: TableProps): Promise<GetDraftNameChangesResponse> {
    const query: DocumentQuery<CustomerNameChange> = {
      sortBy: baseTableProps.sortBy ?? undefined,
      sortOrder: baseTableProps.sortOrder,
      page: baseTableProps.page === undefined ? undefined : baseTableProps.page, // page in the DocService is zero-based.
      pageSize: baseTableProps.pageSize === undefined ? undefined : baseTableProps.pageSize,
      document: {},
    };

    const { data, metadata } = await this.documentService.query<CustomerNameChange>({
      clientId: CLIENT_ID,
      type: DRAFT_NAME_CHANGE_DOCUMENT_TYPE,
      payload: query,
    });
    const { totalCount, totalPages, currentPage } = metadata;

    return { nameChanges: data, totalCount, totalPages, currentPage };
  }

  async getNameChangeDataForMasterCustomerName(customerName: string): Promise<CustomerNameChange[]> {
    const brandSpecificQuery: DocumentQuery<CustomerNameChange> = {
      document: {
        currentCustomerName: [
          {
            operator: 'eq',
            value: customerName,
          },
        ],
      },
    };

    const { data } = await this.documentService.query<CustomerNameChange>({
      clientId: CLIENT_ID,
      type: DRAFT_NAME_CHANGE_DOCUMENT_TYPE,
      payload: brandSpecificQuery,
    });

    return data;
  }
}
